import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CustomInstructionValue } from '@components/forms/custom-instructions-form/custom-instructions-form.component';
import { environment } from '@environments/environment';
import { Idea, IdeaInputParams } from '@models/Sessions/Idea/Idea';
import {
  ScriptEvidence,
  UltimateGenerateRequestArticleTypeEnum,
  UltimateGenerateRequestPromptLanguageEnum,
} from '@models/Sessions/Idea/podcast-api';
import { Session } from '@models/Sessions/Session';
import { PodcastService } from '@services/sessions/podcast.service';
import { MessageService } from 'primeng/api';
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { map, mergeMap, Observable } from 'rxjs';

import { SourcesButtonComponent } from '../../sources/sources-button/sources-button.component';
import { PromptLanguagePickerComponent } from '../components/prompt-language-picker/prompt-language-picker.component';
import { StartManuallyButtonComponent } from '../components/start-manually-button/start-manually-button.component';
import { NewIdeaService } from '../services/new-idea.service';
import { sourcesValidator } from '../validators/sources.validator';
import {
  ArticleSettingsComponent,
  ArticleSettingsPayload,
} from './article-settings-dialog-content/article-settings-dialog-content.component';

interface Form {
  title: FormControl<string | null>;
  sources: FormControl<string[]>;
  customInstruction: FormControl<CustomInstructionValue>;
  promptLanguage: FormControl<UltimateGenerateRequestPromptLanguageEnum>;
  userTextType: FormControl<UltimateGenerateRequestArticleTypeEnum>;
}

@Component({
  selector: 'app-new-article',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ProgressBarModule,
    ButtonModule,
    SelectButtonModule,
    InputTextModule,
    AutoFocusModule,
    InputGroupModule,
    InputGroupAddonModule,
    TextareaModule,
    FloatLabelModule,
    DividerModule,
    StartManuallyButtonComponent,
    PromptLanguagePickerComponent,
    SourcesButtonComponent,
  ],
  providers: [DialogService, NewIdeaService],
  templateUrl: './new-article.component.html',
  styleUrl: './new-article.component.css',
})
export class NewArticleComponent implements OnInit {
  featureFlags = environment.featureFlags;
  isGenerating = signal(false);
  form!: FormGroup<Form>;
  dialogRef: DynamicDialogRef | undefined;
  maxSourceItems = 5;

  @Input() topic: Session;

  get title(): string {
    return this.form.get('title').value;
  }

  get inputParams(): IdeaInputParams {
    const { title, customInstruction, sources, userTextType, promptLanguage } =
      this.form.getRawValue();

    return {
      topic: title,
      inputUrlOrTextList: sources,
      userTextType,
      promptLanguage,
      context: customInstruction.content,
    };
  }

  constructor(
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private ideaService: NewIdeaService,
    private destroyRef: DestroyRef,
    private podcastService: PodcastService,
    private messageService: MessageService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.createForm();
  }

  private createForm() {
    this.form = this.formBuilder.group({
      title: this.formBuilder.control(''),
      customInstruction: this.formBuilder.control<CustomInstructionValue>({
        name: '',
        content: '',
      }),
      sources: this.formBuilder.control<string[]>(
        [],
        sourcesValidator({ maxLength: this.maxSourceItems }),
      ),
      promptLanguage:
        this.formBuilder.control<UltimateGenerateRequestPromptLanguageEnum>(
          'de',
          Validators.required,
        ),
      userTextType:
        this.formBuilder.control<UltimateGenerateRequestArticleTypeEnum>(
          'news_report',
          Validators.required,
        ),
    });
  }

  handleCustomizeClick() {
    const customInstruction = this.form.get('customInstruction').value;
    const promptLanguage = this.form.get('promptLanguage').value;
    const userTextType = this.form.get('userTextType').value;

    // Metadata - use the same validation logic as the main Generate button
    const canGenerate = this.form.valid;

    this.dialogRef = this.dialogService.open(ArticleSettingsComponent, {
      modal: true,
      closable: true,
      contentStyle: { overflow: 'auto', width: '45rem' },
      header: 'Customize',
      closeOnEscape: true,
      dismissableMask: true,

      data: {
        customInstruction,
        promptLanguage,
        userTextType,
        meta: {
          canGenerate,
        },
      },
    });

    this.dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload?: ArticleSettingsPayload) => {
        if (!payload) return;
        const { action, customInstruction, userTextType } = payload;
        this.form.patchValue({ customInstruction, userTextType });

        switch (action) {
          case 'generate':
            return this.goToScript();
        }
      });
  }

  onPromptLanguageChange(lang: UltimateGenerateRequestPromptLanguageEnum) {
    this.form.patchValue({
      promptLanguage: lang,
    });
  }

  onSourcesChange(sources: string[]) {
    this.form.patchValue({
      sources,
    });
  }

  goToScript() {
    this.messageService.clear();
    this.isGenerating.set(true);

    this.generate()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(data =>
          this.ideaService
            .save(this.topic, data)
            .pipe(map((idea): [Idea, string] => [idea, data.inconsistency])),
        ),
      )
      .subscribe({
        next: ([createdIdea, inconsistency]) => {
          this.isGenerating.set(false);
          this.router.navigate(
            ['topics', this.topic._id, 'ideas', createdIdea._id],
            {
              queryParams: { edit: true },
              state: { inconsistency },
            },
          );
        },
        error: () => {
          this.isGenerating.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Something went wrong.',
            detail: 'Please try change input data or try later.',
            sticky: true,
          });
        },
      });
  }

  private generate(): Observable<{
    inputParams: IdeaInputParams;
    script: string;
    shortTitle?: string;
    oneLiner?: string;
    evidence: ScriptEvidence[];
    inconsistency?: string;
  }> {
    const { title, sources, userTextType, customInstruction, promptLanguage } =
      this.form.getRawValue();

    const context = customInstruction.content;

    return this.podcastService
      .ultimateGenerate({
        document_type: 'article',
        topic: title,
        sources,
        context,
        prompt_language: promptLanguage,
        article_type: userTextType,
      })
      .pipe(
        map(data => ({
          inputParams: {
            topic: title,
            inputUrlOrTextList: sources,
            userTextType,
            promptLanguage,
            context,
          },
          script: data.main_text,
          shortTitle: data.short_title,
          oneLiner: data.one_liner,
          evidence: data.evidence_info,
        })),
      );
  }
}
